import { Injectable } from '@angular/core';
import {
  GeolocationPosition,
  GeolocationPositionError,
} from '@models/geolocation.class';
import { PermissionState, PERMISSION_STATES } from '@models/permission.class';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  EMPTY,
  from as fromPromise,
  Observable,
  of,
  throwError as _throw,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AppState, TRACKING_EVENTS } from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { MixpanelService } from './mixpanel.service';

@Injectable()
export class GeolocationService {
  /**
   * Indicates if the user has "granted", or "denied" the Geoloc Permissions,
   * or if they haven't been asked to turn them on ("prompt")
   */
  public permissionsState$ = new BehaviorSubject<PermissionState>(undefined);

  /**
   * The Position object that gets returned by geolocation.getCurrentPosition()
   */
  public position$ = new BehaviorSubject<GeolocationPosition>(undefined);

  /**
   * Indicates whether the notice should be visible or no
   */
  public showNotice$ = new BehaviorSubject<boolean>(false);

  /**
   * Indicates whether the permissions state should be considered to restrict operations
   */
  public shouldRestricGeolocationRelatedOperations$ = new Observable<boolean>();

  constructor(
    private store: Store<AppState>,
    private mixpanelService: MixpanelService,
  ) {
    this.getGeolocationPermissionState();
    this.checkShouldRestricGeolocationRelatedOperations();
  }

  /**
   * Obtains the current position of the device
   *
   * When the PermissionState is:
   *  - "granted", it'll update the PermissionState and the geolocation,
   *  - "denied", it'll update the PermissionState, and set geolocation to undefined,
   *  - "prompt", it'll open the Block/Allow dialog to the user, which triggers "granted"/"denied"
   */
  public promptToEnableGeolocation$(): Observable<PermissionState> {
    if (!featureFlags.enableGeolocationOperations.isEnabled()) {
      return EMPTY;
    }

    if (!navigator.geolocation) {
      this.permissionsState$.next(PERMISSION_STATES.denied);
      this.mixpanelService.addUserProps({ $location_supported: false });
      return _throw({ message: 'Tu dispositivo no puede ser geolocalizado' });
    }

    this.mixpanelService.addUserProps({ $location_supported: true });

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
    };

    return fromPromise(
      /**
       * A new promise is created because getCurrentPosition it's a PromiseLike function that
       * takes succes and error callbacks:
       *
       * getCurrentPosition(successCallback, errorCallback?, options?)
       *
       * This makes it easier to create an Observable from it.
       */
      new Promise<PermissionState>((resolve) => {
        /**
         * If you want to check this locally in Safari or Opera, you need to
         * serve the project with HTTPS. If you can't, you can create a mock
         * position and emit it in the position$ subject:
         *
         *    const MockPos = {...}
         *    this.position$.next(MockPos)
         *    return;
         *
         * To create the mock, check
         * https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPosition
         */
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            this.permissionsState$.next(PERMISSION_STATES.granted);
            this.position$.next(position);
            this.mixpanelService.addUserProps({ $location_granted: true });
            resolve(PERMISSION_STATES.granted);
          },
          (error?: GeolocationPositionError) => {
            this.handleGeolocationPositionError(error, resolve);
          },
          options,
        );
      }),
    );
  }

  /**
   * Obtain the current PermissionState of the geolocation
   * It can be "granted" | "denied" | "prompt"
   */
  public getGeolocationPermissionState(): void {
    /** TODO:
     * --- Remove Navigator interface extension when compatible version of TS includes 'permissions'
     * @see{https://developer.mozilla.org/en-US/docs/Web/API/PermissionState}
     * @see{https://gist.github.com/LayZeeDK/c822cc812f75bb07b7c55d07ba2719b3}
     */
    const { permissions } = navigator as Navigator & { [key: string]: any };

    if (permissions) {
      permissions
        .query({
          name: 'geolocation',
        })
        .then(({ state }: { state: PermissionState }) => {
          this.permissionsState$.next(state);
          if (state === PERMISSION_STATES.granted) {
            this.promptToEnableGeolocation$();
          }
        });

      return;
    }

    /**
     * This is for browsers that haven't or won't implement the permissions
     * API, such as Safari.
     *
     * Check compatibility here:
     * https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API#browser_compatibility
     *
     * ----
     *
     * We return prompt by default because is the most compatible option
     * with our desired workflows, which are:
     *
     * - For users that haven't denied/granted permissions (PROMPT):
     *    Display an EXPLAINNG_MODAL (modal explaining the reasons to why
     *    we require the geolocation) right after the app loads.
     *
     * - For users that have granted permissions (GRANTED):
     *    Obtain the geolocation in a non-intrusive way (no modal).
     *
     * - For users that have denied permissions (DENIED):
     *    Display an EXPLAINING_MODAL right after the app loads, and then
     *    an INSTRUCTIONAL_MODAL when the user opens the modal again on the
     *    same session.
     *
     * PROMPT is the most compatible default state because it only breaks
     * the GRANTED workflow: it'll display an EXPLAINING_MODAL while the
     * geolocation is being fetched (without prompting, because permissions
     * are already granted, but we cannot know).
     *
     * For comparison, using other values as default would break these
     * workflows:
     *
     * GRANTED:
     *  - It'd break both DENIED and PROMPT, because no modal would be
     *    shown, and if its actual value is PROMPT, it'd ask the user to
     *    accept without even showing the modal.
     *
     * DENIED:
     *  - It'd break both GRANTED and PROMPT, because closing and
     *    re-opening a modal would open the INSTRUCTIONAL_MODAL instead of
     *    the EXPLAINING_MODAL.
     *
     * ----
     *
     * Safari will remove the geolocation access on every session by default,
     * and every day if the user asks Safari to remember the 'GRANTED' state.
     *
     * ----------
     * HOWEVER, in any scenario, this will set the permission state when
     * FIRST CHECKING IT.
     * After resolving the geolocation promise, the state gets updated to
     * the real value (GRANTED when the position is successfully fetched,
     * and DENIED when the position cannot be fetched by any reason).
     * ----------
     *
     * So this will only cause issues for
     * - Safari users who launch the app for the 2nd to nth time in the day,
     * - AND asked Safari to remember their GRANTED permission for the day.
     */
    this.permissionsState$.next(PERMISSION_STATES.prompt);
  }

  public setShowNotice(flag: boolean = true) {
    this.showNotice$.next(flag);
  }

  /**
   * Checks if geolocation-dependent workflows should be interrupted
   *
   * This checks for the existance of the enableGeolocationOperations feature flag
   * - When disabled, will always emit false, and no workflow gets interrupted.
   * - When enabled, it'll use the geolocation permission state to set the value.
   */
  private checkShouldRestricGeolocationRelatedOperations() {
    this.shouldRestricGeolocationRelatedOperations$ = this.store
      .select(selectFeatureFlag('enableGeolocationOperations'))
      .pipe(
        filter(
          (enableGeolocationOperations: boolean) =>
            enableGeolocationOperations !== undefined,
        ),
        tap(
          (enableGeolocationOperations) =>
            enableGeolocationOperations &&
            this.permissionsState$.getValue() === PERMISSION_STATES.granted &&
            this.position$.getValue() === undefined &&
            this.promptToEnableGeolocation$(),
        ),
        switchMap((enableGeolocationOperations) =>
          enableGeolocationOperations
            ? this.permissionsState$.pipe(
                // When the ff is enabled, it'll restrict operations any time the permission state is
                // not granted
                map((state) => state !== PERMISSION_STATES.granted),
                switchMap((isDisabled) =>
                  isDisabled
                    ? of(true)
                    : this.position$.pipe(
                        map((position) => !Boolean(position)),
                      ),
                ),
              )
            : of(false),
        ),
        // This will prevent the subject from emitting the same
        // value repeatedly
        distinctUntilChanged(),
      );
  }

  private handleGeolocationPositionError(
    error: GeolocationPositionError,
    resolve: (value: PermissionState | PromiseLike<PermissionState>) => void,
  ) {
    this.permissionsState$.next(PERMISSION_STATES.denied);
    this.position$.next(undefined);

    this.mixpanelService.addUserProps({ $$_location_granted: false });

    if (error.code === error.TIMEOUT) {
      this.mixpanelService.track(
        TRACKING_EVENTS.geolocation_timeout.event,
        TRACKING_EVENTS.geolocation_timeout.payload(error.message),
      );
    } else if (error.code === error.PERMISSION_DENIED) {
      this.mixpanelService.track(
        TRACKING_EVENTS.geolocation_notallowed.event,
        TRACKING_EVENTS.geolocation_notallowed.payload(error.message),
      );
    }

    resolve(PERMISSION_STATES.denied);
  }
}
